// The Art of Revival - Wishlist System
// Manages favorites using localStorage

class AORWishlist {
  constructor() {
    this.storageKey = 'aor_wishlist';
    this.init();
  }

  init() {
    // Update heart states on page load
    this.updateHeartStates();
    
    // Attach click handlers to all heart buttons
    document.addEventListener('click', (e) => {
      const heartBtn = e.target.closest('.wishlist-heart-btn');
      if (heartBtn) {
        e.preventDefault();
        this.toggleProduct(heartBtn);
      }
    });

    // If we're on the favorites page, render the items
    if (window.location.pathname.includes('/pages/favorites') || 
        window.location.pathname.includes('/pages/wishlist')) {
      this.renderFavoritesPage();
    }

    // Update heart count in header if you add a counter later
    this.updateHeaderCount();
  }

  getWishlist() {
    const stored = localStorage.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : [];
  }

  saveWishlist(wishlist) {
    localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
  }

  isInWishlist(productId) {
    const wishlist = this.getWishlist();
    return wishlist.some(item => item.id === productId);
  }

  toggleProduct(heartBtn) {
    const productData = {
      id: heartBtn.dataset.productId,
      handle: heartBtn.dataset.productHandle,
      title: heartBtn.dataset.productTitle,
      price: heartBtn.dataset.productPrice,
      image: heartBtn.dataset.productImage,
      url: heartBtn.dataset.productUrl,
      addedAt: new Date().toISOString()
    };

    let wishlist = this.getWishlist();
    const existingIndex = wishlist.findIndex(item => item.id === productData.id);

    if (existingIndex > -1) {
      // Remove from wishlist
      wishlist.splice(existingIndex, 1);
      this.updateHeartButton(heartBtn, false);
    } else {
      // Add to wishlist
      wishlist.push(productData);
      this.updateHeartButton(heartBtn, true);
    }

    this.saveWishlist(wishlist);
    this.updateHeaderCount();

    // If we're on the favorites page, re-render
    if (window.location.pathname.includes('/pages/favorites') || 
        window.location.pathname.includes('/pages/wishlist')) {
      this.renderFavoritesPage();
    }
  }

  updateHeartButton(button, isActive) {
    const outline = button.querySelector('.heart-outline');
    const fill = button.querySelector('.heart-fill');
    
    if (isActive) {
      button.classList.add('is-active');
      outline.style.display = 'none';
      fill.style.display = 'block';
    } else {
      button.classList.remove('is-active');
      outline.style.display = 'block';
      fill.style.display = 'none';
    }
  }

  updateHeartStates() {
    document.querySelectorAll('.wishlist-heart-btn').forEach(btn => {
      const productId = btn.dataset.productId;
      const isActive = this.isInWishlist(productId);
      this.updateHeartButton(btn, isActive);
    });
  }

  updateHeaderCount() {
    const wishlist = this.getWishlist();
    const count = wishlist.length;
    
    // Update count badge if it exists in your header
    const countBadge = document.querySelector('.wishlist-count');
    if (countBadge) {
      countBadge.textContent = count;
      countBadge.style.display = count > 0 ? 'block' : 'none';
    }
  }

  renderFavoritesPage() {
    const container = document.querySelector('[data-wishlist-container]');
    if (!container) return;

    const wishlist = this.getWishlist();

    if (wishlist.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <h1>Start heartin'</h1>
          <p>Heart pieces as you browse. They'll live here until you're ready.</p>
          <a href="/collections/all" class="btn-browse-collection">Browse the Collection</a>
        </div>
      `;
      return;
    }

    // Render products in grid
    const productsHTML = wishlist.map(product => {
      const priceFormatted = (parseInt(product.price) / 100).toFixed(2);
      return `
        <div class="wishlist-product-card">
          <div class="wishlist-product-image">
            <a href="${product.url}">
              <img src="${product.image}" alt="${product.title}" loading="lazy">
            </a>
            <button 
              class="wishlist-heart-btn is-active" 
              data-product-id="${product.id}"
              data-product-handle="${product.handle}"
              data-product-title="${product.title}"
              data-product-price="${product.price}"
              data-product-image="${product.image}"
              data-product-url="${product.url}"
              aria-label="Remove from favorites"
              type="button"
            >
              <svg class="wishlist-heart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="heart-outline" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: none;"/>
                <path class="heart-fill" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div class="wishlist-product-info">
            <a href="${product.url}" class="wishlist-product-title">${product.title}</a>
            <p class="wishlist-product-price">$${priceFormatted}</p>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="wishlist-header">
        <h1>Your Favorites</h1>
        <p class="wishlist-count-text">${wishlist.length} ${wishlist.length === 1 ? 'piece' : 'pieces'}</p>
      </div>
      <div class="wishlist-grid">
        ${productsHTML}
      </div>
    `;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.aorWishlist = new AORWishlist();
  });
} else {
  window.aorWishlist = new AORWishlist();
}